import * as Yup from 'yup';
import { isValidPhoneNumber } from 'react-phone-number-input';

import {
  FormField,
  InputType,
  InputOptions,
  RadioOptions,
  FormFieldType,
  SelectOptions,
  CheckboxOptions,
  CsvLookupPreset,
  FieldArrayLayout,
  FormChoiceResponse,
  ScaleRatingOptions,
  OutcomesFieldOptions,
  OutcomeFieldsOptions,
  FormChoiceResponseType,
} from 'types';

import { urlPattern } from './regex';
import { isSingleScaleRating } from './scale-rating';
import { getResourceRichTextError } from './resource-rich-text';
import { questionLabelText, hasVisibleQuestionLabel } from './question-label';
import {
  fieldArrayDisallowedNestedFieldTypes,
  outcomeFieldsDisallowedNestedFieldTypes,
} from './form';

type ChoiceOptions = Pick<
  RadioOptions,
  'options' | 'randomize' | 'allowOther' | 'answerValues' | 'otherAnswerValue'
>;

export const password = Yup.string().trim().min(6).required();

export const confirmPassword = password.test(
  'passwordRepeat',
  'Passwords must match',
  function (value) {
    return this.parent.password === value;
  }
);

export const color = (allowShortHex = true) =>
  Yup.string().matches(
    allowShortHex ? /^#([0-9A-F]{3}){1,2}$/i : /^#[0-9A-F]{6}$/i,
    allowShortHex
      ? 'Must be a valid hex color (e.g., #FF0000 or #F00)'
      : 'Must be a valid six-character hex color (e.g., #FF0000)'
  );

export const phoneNumber = (name: string) =>
  Yup.string()
    .nullable()
    .test(name, 'Invalid phone number', function (value) {
      return value && value.length > 0 ? isValidPhoneNumber(value) : true;
    });

export const url = Yup.string().matches(urlPattern, 'Enter a valid url');
export const ssoIdentifierPattern = /^[a-z0-9_-]+$/;
export const ssoIdentifierPatternMessage =
  'Allowed: lowercase letters, numbers, hyphen (-), underscore (_)';

export const passwordValidationRules: Record<
  string,
  (value: string) => boolean
> = {
  '8 characters': (value) => value.length >= 8,
  '1 number': (value) => /^(?=.*[0-9])/.test(value),
  '1 lowercase': (value) => /^(?=.*[a-z])/.test(value),
  '1 uppercase': (value) => /^(?=.*[A-Z])/.test(value),
};

export { isValidPhoneNumber };

export const generateFormFieldsValidationSchema = (
  fields: FormField[]
): Yup.ObjectSchema<any> => {
  let schemaFields: Record<string, Yup.AnySchema> = {};

  fields.forEach((field) => {
    schemaFields = createFieldValidation(field, schemaFields);
  });

  return Yup.object().shape(schemaFields);
};

const createFieldValidation = (
  field: FormField,
  schemaFields: Record<string, Yup.AnySchema>
): Record<string, Yup.AnySchema> => {
  if (field.type === FormFieldType.FIELD_ARRAY) {
    let schema = {
      ...schemaFields,
    };
    field.options?.fields.forEach((nestedField) => {
      schema = createFieldValidation(nestedField, schema);
    });
    return schema;
  }

  if (field.type === FormFieldType.OUTCOME_FIELDS) {
    let schema = {
      ...schemaFields,
    };
    field.options?.fields.forEach((nestedField) => {
      schema = createFieldValidation(nestedField, schema);
    });
    return schema;
  }

  let validation: Yup.AnySchema;

  switch (field.type) {
    case FormFieldType.INPUT:
      validation = createInputValidation(field.options as InputOptions);
      break;

    case FormFieldType.SELECT:
      validation = createSelectValidation(field.options as SelectOptions);
      break;

    case FormFieldType.RADIO:
      validation = createRadioValidation(field.options as RadioOptions);
      break;

    case FormFieldType.CHECKBOX:
      validation = createCheckboxValidation(
        field.options as CheckboxOptions,
        field.required
      );
      break;

    case FormFieldType.SCALE_RATING:
      validation = createScaleRatingValidation(
        field.options as ScaleRatingOptions,
        field.required
      );
      break;

    case FormFieldType.OUTCOMES:
      validation = Yup.object({
        orderedIds: Yup.array().of(Yup.string().required()).required(),
        outcomes: Yup.array()
          .of(
            Yup.object({
              id: Yup.string().required(),
              label: Yup.string().required(),
              description: Yup.string().optional(),
            })
          )
          .required(),
      });
      break;

    case FormFieldType.TEXT:
      // Static content has no answer key to validate.
      return schemaFields;

    case FormFieldType.PAGE_BREAK:
    case FormFieldType.HEADING:
      validation = Yup.object();
      break;

    case FormFieldType.CSV_LOOKUP: {
      const csvLookupValidation = Yup.array();
      validation = field.required
        ? csvLookupValidation.min(1, 'Field is required')
        : csvLookupValidation;
      break;
    }
  }

  if (field.required) {
    validation = validation.required('Field is required');
  } else {
    validation = validation.nullable().optional();
  }

  return {
    ...schemaFields,
    [field.id]: validation,
  };
};

const createInputValidation = (options?: InputOptions): Yup.AnySchema => {
  if (!options) {
    return Yup.string();
  }

  let validation:
    | Yup.StringSchema
    | Yup.NumberSchema
    | Yup.DateSchema<Date | null | undefined>;

  if (options.type === InputType.Date) {
    validation = Yup.date()
      .nullable()
      .transform((value, originalValue) =>
        originalValue == null || originalValue === '' ? null : value
      )
      .typeError('Must be a valid date');
  } else if (options.type === InputType.Number) {
    validation = Yup.number().typeError('Must be a number');

    if (options.minValue !== undefined) {
      validation = validation.min(
        options.minValue,
        `Must be at least ${options.minValue}`
      );
    }

    if (options.maxValue !== undefined) {
      validation = validation.max(
        options.maxValue,
        `Must be at most ${options.maxValue}`
      );
    }

    if (options.integer) {
      validation = validation.integer('Must be an integer');
    }
  } else {
    validation = Yup.string();

    if (options.type === 'email') {
      validation = validation.email('Must be a valid email address');
    }

    if (options.minLength !== undefined) {
      validation = validation.min(
        options.minLength,
        `Must be at least ${options.minLength} characters`
      );
    }

    if (options.maxLength !== undefined) {
      validation = validation.max(
        options.maxLength,
        `Must be at most ${options.maxLength} characters`
      );
    }
  }

  return validation;
};

const createSelectValidation = (options?: SelectOptions): Yup.AnySchema => {
  if (!options || !Array.isArray(options.options)) {
    return Yup.mixed();
  }

  const hasCorrectOptions =
    options.correctOptions && options.correctOptions.length > 0;

  if (options.multiple) {
    let schema = createChoiceArrayValidation(options).min(
      1,
      'At least one option must be selected'
    );
    if (hasCorrectOptions) {
      schema = schema.test(
        'correct-selection',
        'Selected options are not correct',
        function (selectedValues) {
          if (!selectedValues || !Array.isArray(selectedValues)) return true;

          const selectedIndexes = selectedValues.map((value) =>
            isFormChoiceResponse(value) &&
            value.type === FormChoiceResponseType.OPTION
              ? options.options.indexOf(value.value)
              : -1
          );

          return selectedIndexes.every((idx) =>
            options.correctOptions!.includes(idx)
          );
        }
      );
    }
    return schema;
  } else {
    let schema = createSingleChoiceValidation(options);
    if (hasCorrectOptions) {
      schema = schema.test(
        'correct-selection',
        'Selected option is not correct',
        function (selectedValue) {
          if (!selectedValue) return true;

          const selectedIndex =
            isFormChoiceResponse(selectedValue) &&
            selectedValue.type === FormChoiceResponseType.OPTION
              ? options.options.indexOf(selectedValue.value)
              : -1;

          return options.correctOptions!.includes(selectedIndex);
        }
      );
    }
    return schema;
  }
};

const createRadioValidation = (options?: RadioOptions): Yup.AnySchema => {
  if (!options || !Array.isArray(options.options)) {
    return Yup.mixed();
  }
  return createSingleChoiceValidation(options);
};

const createSingleChoiceValidation = (options: ChoiceOptions) =>
  Yup.mixed()
    .test(
      'other-specified',
      'Please specify your answer',
      (value) => !isEmptyOtherResponse(value)
    )
    .test(
      'valid-option',
      `Must be one of: ${options.options.join(', ')}`,
      (value) => !value || isChoiceResponseAllowed(value, options)
    );

const createChoiceArrayValidation = (options: ChoiceOptions) =>
  Yup.array()
    .of(Yup.mixed().required())
    .test(
      'other-specified',
      'Please specify your answer',
      (values) => !(values ?? []).some(isEmptyOtherResponse)
    )
    .test('valid-options', 'Invalid option selected', (values) => {
      const otherValues = (values ?? []).filter(
        (value) =>
          isFormChoiceResponse(value) &&
          value.type === FormChoiceResponseType.OTHER
      );
      return (
        otherValues.length <= 1 &&
        (values ?? []).every((value) => isChoiceResponseAllowed(value, options))
      );
    });

const createCheckboxValidation = (
  options?: CheckboxOptions,
  isRequired?: boolean
): Yup.AnySchema => {
  if (!options || !Array.isArray(options.options)) {
    return Yup.mixed();
  }

  let validation = createChoiceArrayValidation(options);

  if (isRequired) {
    validation = validation.min(1, 'At least one option must be selected');
  }

  return validation;
};

const isFormChoiceResponse = (value: unknown): value is FormChoiceResponse =>
  Boolean(
    value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.values(FormChoiceResponseType).includes(
      (value as FormChoiceResponse).type
    ) &&
    typeof (value as FormChoiceResponse).value === 'string'
  );

const isOtherResponse = (
  value: unknown
): value is { value?: unknown; type: FormChoiceResponseType.OTHER } =>
  Boolean(
    value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    (value as Partial<FormChoiceResponse>).type === FormChoiceResponseType.OTHER
  );

const isEmptyOtherResponse = (value: unknown): boolean =>
  isOtherResponse(value) &&
  (value.value == null ||
    (typeof value.value === 'string' && !value.value.trim()));

const isChoiceResponseAllowed = (
  value: unknown,
  options: ChoiceOptions
): boolean =>
  isEmptyOtherResponse(value) ||
  (isFormChoiceResponse(value) &&
    (value.type === FormChoiceResponseType.OPTION
      ? options.options.includes(value.value)
      : Boolean(options.allowOther)));

const createScaleRatingValidation = (
  options?: ScaleRatingOptions,
  isRequired?: boolean
): Yup.AnySchema => {
  if (!options || !Array.isArray(options.options) || !options.options.length) {
    return Yup.object();
  }
  const { minValue, maxValue } = options;

  const validation = Yup.array().of(
    Yup.number()
      .min(minValue, `Rating must be at least ${minValue}`)
      .max(maxValue, `Rating must be at most ${maxValue}`)
      .when([], {
        is: () => isRequired,
        otherwise: (schema) => schema.nullable().optional(),
        then: (schema) => schema.required(`Rating is required`),
      })
  );

  return validation;
};

export const fieldArrayOptionsValidationSchema = (
  fieldValidationSchema: Yup.AnyObjectSchema
) =>
  Yup.object({
    layout: Yup.string<FieldArrayLayout>()
      .oneOf(Object.values(FieldArrayLayout))
      .required('Layout is required'),
    fields: Yup.array()
      .of(fieldValidationSchema)
      .min(1)
      .required()
      .test(
        'no-outcome-types-in-field-array',
        'Outcomes and outcome fields are not allowed inside field arrays',
        (fields) =>
          !fields?.some(
            (field) =>
              field?.type !== undefined &&
              fieldArrayDisallowedNestedFieldTypes.has(field.type)
          )
      ),
  });

const typeSchema = Yup.string<InputType>()
  .oneOf(Object.values(InputType))
  .required('Input type is required');

const textOptionsSchema = Yup.object({
  type: typeSchema,
  maxLength: Yup.number()
    .positive('Max length must be a positive number')
    .min(1, 'Max length must be at least 1'),
  minLength: Yup.number()
    .positive('Min length must be a positive number')
    .min(0, 'Min length cannot be negative')
    .test(
      'minLength<=maxLength',
      'Min length must be less than or equal to max length',
      function (value) {
        const { maxLength } = this.parent;
        return value! <= maxLength!;
      }
    ),
});

const numberOptionsSchema = Yup.object({
  type: typeSchema,
  integer: Yup.boolean().required('Integer flag is required for number inputs'),
  minValue: Yup.number().test(
    'minValue<=maxValue',
    'Min value must be less than or equal to max value',
    function (value) {
      const { maxValue } = this.parent;
      return typeof maxValue === 'number' ? value! <= maxValue! : true;
    }
  ),
  maxValue: Yup.number().test(
    'maxValue>=minValue',
    'Max value must be greater than or equal to min value',
    function (value) {
      const { minValue } = this.parent;
      return typeof minValue === 'number' ? value! >= minValue! : true;
    }
  ),
});

export function uniqueArrayTest(
  this: any,
  value: string[] | undefined
): boolean | Yup.ValidationError {
  if (!value) return true;

  const seen = new Set<string>();
  for (let i = 0; i < value.length; i++) {
    if (seen.has(value[i])) {
      return this.createError({
        path: `${this.path}[${i}]`,
        message: `"${value[i]}" already exists`,
      });
    }
    seen.add(value[i]);
  }
  return true;
}

function uniqueOptionalTextArrayTest(
  this: any,
  value: Array<string | undefined> | undefined
): boolean | Yup.ValidationError {
  if (!value) return true;

  const seen = new Set<string>();
  for (let i = 0; i < value.length; i++) {
    const item = value[i]?.trim();
    if (!item) {
      continue;
    }

    if (seen.has(item)) {
      return this.createError({
        path: `${this.path}[${i}]`,
        message: `"${item}" already exists`,
      });
    }
    seen.add(item);
  }
  return true;
}

interface ReferenceCodeEntry {
  code: string;
  path: string;
}

const collectReferenceCodes = (
  fields: FormField[] | undefined,
  fieldsPath: string
): ReferenceCodeEntry[] =>
  (fields ?? []).flatMap((field, index) => {
    const fieldPath = `${fieldsPath}[${index}]`;
    const code = field.referenceCode?.trim();
    const current = code ? [{ code, path: `${fieldPath}.referenceCode` }] : [];

    if (field.type === FormFieldType.FIELD_ARRAY) {
      return [
        ...current,
        ...collectReferenceCodes(
          field.options?.fields,
          `${fieldPath}.options.fields`
        ),
      ];
    }

    if (field.type === FormFieldType.OUTCOME_FIELDS) {
      return [
        ...current,
        ...collectReferenceCodes(
          field.options?.fields,
          `${fieldPath}.options.fields`
        ),
      ];
    }

    return current;
  });

const uniqueReferenceCodesTest = function (
  this: any,
  blocks: Array<{ fields?: FormField[] }> | undefined
): boolean | Yup.ValidationError {
  const seen = new Set<string>();
  const entries = (blocks ?? []).flatMap((block, blockIndex) =>
    collectReferenceCodes(block.fields, `${this.path}[${blockIndex}].fields`)
  );

  for (const entry of entries) {
    if (seen.has(entry.code)) {
      return this.createError({
        path: entry.path,
        message: `"${entry.code}" already exists`,
      });
    }
    seen.add(entry.code);
  }

  return true;
};

const inputOptionsSchemaMap: Record<InputType, Yup.ObjectSchema<any>> = {
  [InputType.Date]: Yup.object(),
  [InputType.Email]: Yup.object(),
  [InputType.Text]: textOptionsSchema,
  [InputType.Number]: numberOptionsSchema,
};

export const inputOptionsValidationSchema = Yup.lazy<any, any>(
  (value) =>
    inputOptionsSchemaMap[value.type as InputType] ??
    Yup.object().nullable().optional()
);

function uniqueChoiceAnswerValuesTest(
  this: Yup.TestContext,
  value: Array<string | undefined> | undefined
): boolean | Yup.ValidationError {
  const regularValues = uniqueOptionalTextArrayTest.call(this, value);
  if (regularValues !== true) return regularValues;
  const { allowOther, otherAnswerValue } = this.parent as ChoiceOptions;
  const otherValue = otherAnswerValue?.trim();
  if (!allowOther || !otherValue) return true;
  const index = value?.findIndex((entry) => entry?.trim() === otherValue) ?? -1;
  return index < 0
    ? true
    : this.createError({
        path: `${this.path}[${index}]`,
        message: `"${otherValue}" already exists`,
      });
}

const otherAnswerValueSchema = Yup.string()
  .optional()
  .test('unique-other-answer-value', 'Values must be unique', function (value) {
    const { allowOther, answerValues } = this.parent as ChoiceOptions;
    const normalizedValue = value?.trim();
    if (
      allowOther &&
      normalizedValue &&
      answerValues?.some(
        (answerValue) => answerValue?.trim() === normalizedValue
      )
    ) {
      return this.createError({
        message: `"${normalizedValue}" already exists`,
      });
    }
    return true;
  });

export const selectOptionsValidationSchema = Yup.object<SelectOptions>({
  randomize: Yup.boolean().optional(),
  allowOther: Yup.boolean().optional(),
  otherAnswerValue: otherAnswerValueSchema,
  multiple: Yup.boolean().required('Multiple is a required field'),
  answerValues: Yup.array()
    .of(Yup.string())
    .optional()
    .test(
      'unique-answer-values',
      'Values must be unique',
      uniqueChoiceAnswerValuesTest
    ),
  explanation: Yup.string().when('correctOptions', {
    otherwise: (schema) => schema.notRequired(),
    is: (correctOptions?: number[]) => !!correctOptions,
    then: (schema) => schema.required('Explanation is a required field'),
  }),
  options: Yup.array()
    .of(Yup.string().required('Option is a required field'))
    .min(1, 'At least one option is required')
    .test('unique-options', 'Options must be unique', uniqueArrayTest)
    .required('Options are required'),
  correctOptions: Yup.array()
    .of(Yup.number().required())
    .optional()
    .test(
      'valid-correct-length',
      'At least one correct option is required',
      function (correctOptions) {
        if (!correctOptions) return true;

        return correctOptions.length > 0;
      }
    )
    .test(
      'valid-correct-options',
      'Correct options must be from available options',
      function (correctOptions) {
        if (!correctOptions) return true;

        const { options } = this.parent;
        return correctOptions.every((correct) => correct <= options.length);
      }
    )
    .test(
      'single-select-validation',
      'Single select can only have one correct option',
      function (correctOptions) {
        if (!correctOptions) return true;

        const { multiple } = this.parent;
        if (!multiple && correctOptions.length > 1) {
          return this.createError({
            message: 'Single select questions can only have one correct option',
          });
        }

        return true;
      }
    ),
});

const createChoiceOptionsValidationSchema = <T extends ChoiceOptions>() =>
  Yup.object<T>({
    randomize: Yup.boolean().optional(),
    allowOther: Yup.boolean().optional(),
    otherAnswerValue: otherAnswerValueSchema,
    answerValues: Yup.array()
      .of(Yup.string())
      .optional()
      .test(
        'unique-answer-values',
        'Values must be unique',
        uniqueChoiceAnswerValuesTest
      ),
    options: Yup.array()
      .of(Yup.string().required('Option is a required field'))
      .min(1, 'At least one option is required')
      .test('unique-options', 'Options must be unique', uniqueArrayTest)
      .required('Options are required'),
  });

export const radioOptionsValidationSchema =
  createChoiceOptionsValidationSchema<RadioOptions>();

export const checkboxOptionsValidationSchema =
  createChoiceOptionsValidationSchema<CheckboxOptions>();

export const scaleRatingOptionsValidationSchema =
  Yup.object<ScaleRatingOptions>({
    _rowsEnabled: Yup.boolean().optional(),
    minValue: Yup.number()
      .required('Min value is a required field')
      .integer('Min value must be an integer')
      .min(0, 'Min value must be at least 0'),
    maxValue: Yup.number()
      .required('Max value is a required field')
      .integer('Max value must be an integer')
      .positive('Max value must be a positive number')
      .test('max>min', 'Max value must be greater than Min', function (value) {
        const { minValue } = this.parent;
        return value! > minValue!;
      }),
    valueLabels: Yup.array()
      .of(Yup.string())
      .test(
        'value-labels-max-length',
        'Value labels count cannot exceed scale size',
        function (value) {
          if (!value) {
            return true;
          }
          const { minValue, maxValue } = this.parent as ScaleRatingOptions;
          return value.length <= maxValue - minValue + 1;
        }
      )
      .optional(),
    answerCodes: Yup.array()
      .of(Yup.string())
      .optional()
      .test(
        'unique-answer-codes',
        'Answer codes must be unique',
        function (value) {
          const { options, _rowsEnabled } = this.parent as ScaleRatingOptions;
          // Ignore codes that payload normalization omits, including legacy tails.
          const count =
            !_rowsEnabled && isSingleScaleRating(options)
              ? 0
              : (options?.length ?? 0);
          return uniqueOptionalTextArrayTest.call(this, value?.slice(0, count));
        }
      ),
    answerValues: Yup.array()
      .of(Yup.string())
      .optional()
      .test(
        'scale-values-max-length',
        'Values count cannot exceed scale size',
        function (value) {
          const { minValue, maxValue } = this.parent as ScaleRatingOptions;
          if (
            !Number.isInteger(minValue) ||
            !Number.isInteger(maxValue) ||
            minValue < 0 ||
            maxValue <= minValue
          )
            return true;
          return !value || value.length <= maxValue - minValue + 1;
        }
      )
      .test(
        'unique-answer-values',
        'Values must be unique',
        uniqueOptionalTextArrayTest
      ),
    options: Yup.array()
      .of(Yup.string().optional())
      .test('row-labels', 'Option is a required field', function (items) {
        if (
          !this.parent._rowsEnabled &&
          isSingleScaleRating(items?.map((item) => item ?? ''))
        ) {
          return true;
        }
        const emptyIndex =
          items?.findIndex((item) => !hasVisibleQuestionLabel(item)) ?? -1;
        return (
          emptyIndex < 0 ||
          this.createError({
            path: `${this.path}[${emptyIndex}]`,
            message: 'Option is a required field',
          })
        );
      })
      .min(1, 'At least one option is required')
      .test('unique-options', 'Options must be unique', function (items) {
        return uniqueArrayTest.call(
          this,
          items?.map((item) => questionLabelText(item ?? ''))
        );
      })
      .required('Options are required'),
  });

export const outcomesOptionsValidationSchema = Yup.object<OutcomesFieldOptions>(
  {
    csvContent: Yup.string().optional(),
    conditionValue: Yup.string().required(
      'Condition value is a required field'
    ),
    outcomes: Yup.array()
      .of(
        Yup.object({
          description: Yup.string().optional(),
          id: Yup.string().required('Outcome ID is required'),
          label: Yup.string().required('Outcome label is required'),
        })
      )
      .test(
        'unique-outcome-ids',
        'Outcome IDs must be unique',
        function (outcomes) {
          const ids = outcomes?.map((outcome) => outcome.id) ?? [];
          return uniqueArrayTest.call(this, ids);
        }
      )
      .when('csvContent', {
        is: (csvContent?: string) => !csvContent,
        otherwise: (schema) => schema.optional(),
        then: (schema) => schema.min(1, 'Upload outcomes CSV').required(),
      }),
    conditions: Yup.array()
      .of(
        Yup.object({
          matchingValue: Yup.string(),
          outcomeIds: Yup.array()
            .of(Yup.string().required())
            .min(1, 'Each condition must reference at least one outcome')
            .required('Each condition must reference at least one outcome'),
        })
      )
      .test(
        'single-empty-matching-value',
        'Only one condition can have an empty matching value',
        (conditions) =>
          (conditions?.filter((condition) => condition?.matchingValue === '')
            .length ?? 0) <= 1
      )
      .when('csvContent', {
        is: (csvContent?: string) => !csvContent,
        otherwise: (schema) => schema.optional(),
        then: (schema) => schema.min(1, 'Upload outcomes CSV').required(),
      }),
  }
);

export const outcomeFieldsOptionsValidationSchema = (
  fieldValidationSchema: Yup.AnyObjectSchema
) =>
  Yup.object<OutcomeFieldsOptions>({
    sourceFieldId: Yup.string().required('Source field is a required field'),
    sourceCourseStepId: Yup.string().required(
      'Source step is a required field'
    ),
    maxOutcomeCount: Yup.number()
      .transform((value, originalValue) =>
        originalValue === '' || originalValue === null ? undefined : value
      )
      .integer('Max outcomes must be an integer')
      .min(1, 'Max outcomes must be at least 1')
      .optional(),
    fields: Yup.array()
      .of(fieldValidationSchema)
      .min(1, 'At least one nested field is required')
      .required('Nested fields are required')
      .test(
        'no-outcome-types-in-outcome-fields',
        'Outcomes, outcome fields, and page breaks are not allowed inside outcome fields',
        (fields) =>
          !fields?.some(
            (field) =>
              field?.type !== undefined &&
              outcomeFieldsDisallowedNestedFieldTypes.has(field.type)
          )
      ),
  });

export const placeholderHiddenFields = new Set([
  FormFieldType.FIELD_ARRAY,
  FormFieldType.RADIO,
  FormFieldType.CHECKBOX,
  FormFieldType.SCALE_RATING,
  FormFieldType.OUTCOMES,
  FormFieldType.OUTCOME_FIELDS,
  FormFieldType.PAGE_BREAK,
  FormFieldType.HEADING,
  FormFieldType.TEXT,
]);

export const labelHiddenFields = new Set([
  FormFieldType.PAGE_BREAK,
  FormFieldType.TEXT,
]);

export const requiredHiddenFields = new Set([
  FormFieldType.OUTCOMES,
  FormFieldType.OUTCOME_FIELDS,
  FormFieldType.PAGE_BREAK,
  FormFieldType.HEADING,
  FormFieldType.TEXT,
]);

const fieldValidationSchema: Yup.ObjectSchema<any> = Yup.object().shape({
  subtitle: Yup.string().optional(),
  referenceCode: Yup.string().optional(),
  required: Yup.boolean().required('Required is a required field'),
  type: Yup.mixed<FormFieldType>()
    .oneOf(Object.values(FormFieldType))
    .required('Type is a required field'),
  placeholder: Yup.string().when('type', {
    otherwise: (schema) => schema.optional(),
    is: (type: FormFieldType) => !placeholderHiddenFields.has(type),
    then: (schema) => schema.required('Placeholder is a required field'),
  }),
  text: Yup.string().when('type', {
    is: FormFieldType.TEXT,
    otherwise: (schema) => schema.optional(),
    then: (schema) =>
      schema.test('rich-text', 'Enter text', (value, context) => {
        const error = getResourceRichTextError(value);
        return error ? context.createError({ message: error }) : true;
      }),
  }),
  inputId: Yup.mixed()
    .transform((value, originalValue) =>
      originalValue === '' ? undefined : value
    )
    .test(
      'input-id-type',
      'Input ID must be a string or number',
      (value) =>
        value === undefined ||
        value === null ||
        typeof value === 'string' ||
        typeof value === 'number'
    )
    .optional(),
  label: Yup.string()
    .trim()
    .test('visible-label', 'Label must contain text', function (value) {
      return (
        !value ||
        hasVisibleQuestionLabel(value) ||
        this.createError({
          message:
            this.parent.type === FormFieldType.HEADING
              ? 'Title must contain text'
              : 'Label must contain text',
        })
      );
    })
    .when('type', ([type], schema) =>
      type === FormFieldType.TEXT
        ? schema.optional()
        : schema.required(
            type === FormFieldType.HEADING
              ? 'Title is a required field'
              : 'Label is a required field'
          )
    ),
  options: Yup.lazy((_, { parent }) => {
    const optionsSchemas: Record<FormFieldType, Yup.AnySchema | Yup.Lazy<any>> =
      {
        [FormFieldType.TEXT]: Yup.object(),
        [FormFieldType.HEADING]: Yup.object(),
        [FormFieldType.PAGE_BREAK]: Yup.object(),
        [FormFieldType.INPUT]: inputOptionsValidationSchema,
        [FormFieldType.RADIO]: radioOptionsValidationSchema,
        [FormFieldType.SELECT]: selectOptionsValidationSchema,
        [FormFieldType.CHECKBOX]: checkboxOptionsValidationSchema,
        [FormFieldType.OUTCOMES]: outcomesOptionsValidationSchema,
        [FormFieldType.SCALE_RATING]: scaleRatingOptionsValidationSchema,
        [FormFieldType.FIELD_ARRAY]: fieldArrayOptionsValidationSchema(
          fieldValidationSchema
        ),
        [FormFieldType.OUTCOME_FIELDS]: outcomeFieldsOptionsValidationSchema(
          fieldValidationSchema
        ),
        [FormFieldType.CSV_LOOKUP]: Yup.object({
          sourceFile: Yup.mixed<File>().optional(),
          datasetId: Yup.string().uuid().optional(),
          preset: Yup.string().oneOf(Object.values(CsvLookupPreset)).required(),
        }).test('csv-source', 'Select a CSV file', (options) =>
          Boolean(options?.datasetId || options?.sourceFile)
        ),
      };

    const schema = optionsSchemas[parent.type as FormFieldType];
    return schema ?? Yup.mixed();
  }),
});

export const blockValidationSchema = Yup.object().shape({
  name: Yup.string(),
  id: Yup.string().required(),
  fields: Yup.array()
    .of(fieldValidationSchema)
    .min(1)
    .required()
    .test(
      'page-break-validation',
      'Page breaks must have fields before and after',
      function (fields, { path }) {
        if (!fields || fields.length === 0) return true;

        for (let i = 0; i < fields.length; i++) {
          const field = fields[i];

          if (field.type === FormFieldType.PAGE_BREAK) {
            // Check if page break is at the beginning
            if (i === 0) {
              return this.createError({
                path: `${path}[${i}]`,
                message: 'Page break cannot be the first field',
              });
            }

            // Check if page break is at the end
            if (i === fields.length - 1) {
              return this.createError({
                path: `${path}[${i}]`,
                message: 'Page break cannot be the last field',
              });
            }

            // Check for consecutive page breaks
            if (i > 0 && fields[i - 1].type === FormFieldType.PAGE_BREAK) {
              return this.createError({
                path: `${path}[${i}]`,
                message: 'Page break cannot follow another page break',
              });
            }
          }
        }

        return true;
      }
    ),
});

export const blocksValidation = Yup.array()
  .of(blockValidationSchema)
  .min(1)
  .required()
  .test(
    'unique-reference-codes',
    'Question codes must be unique',
    uniqueReferenceCodesTest
  );

export const zipCode = (isRequired?: boolean) => {
  let schema = Yup.string().matches(/^\d{5}(-\d{4})?$/, {
    excludeEmptyString: true,
    message: 'ZIP code must be in format: 12345 or 12345-6789',
  });

  if (isRequired) {
    schema = schema.required();
  }

  return schema;
};
